import {
  Plus,
  Download,
  RefreshCw,
  ArrowUpRight,
  AlertTriangle,
  BarChart3,
  CheckSquare,
  Square,
  MessageCircle,
  Search,
  Send,
} from 'lucide-react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import EmptyState from '../../components/EmptyState';
import { EmptyFund } from '../../components/illustrations';
import StatusBadge from '../../components/ui/status-badge';
import NeoPayBadge from '../../components/NeoPayBadge';
import AIApprovalChip from '../../components/AIApprovalChip';
import CommentThread from '../../components/CommentThread';
import NXCIcon from '../../components/NXCIcon';
import PendingGuestFeesCard from '../../components/PendingGuestFeesCard';
import { formatNXC } from '../../lib/formatNXC';
import api from '../../lib/api';
import { toast } from 'sonner';

// iter297 — fund-effect helpers, faithful mirror of backend exports_ledger._fund_dr_cr.
// Direction: which way money flows for DISPLAY (+/-/neutral).
const FUND_IN_TYPES = ['contribution', 'guest_contribution', 'session_fee', 'membership_fee', 'fine', 'expense_reversal'];
const FUND_OUT_TYPES = ['expense_deduct', 'expense_advance', 'fine_reversal', 'fund_reimburse', 'loan_disbursed', 'cash_out',
  'session_fee_reversal', 'membership_fee_reversal', 'contribution_reversal', 'guest_contribution_reversal'];
// Reversal rows whose ORIGINAL is flagged reversed → the pair nets zero, row itself has no effect.
const ZERO_EFFECT_REVERSALS = ['session_fee_reversal', 'membership_fee_reversal', 'contribution_reversal', 'guest_contribution_reversal'];

function fundDirection(t) {
  if (t.type === 'loan_repayment') return (t.loan_direction || 'member_to_group') === 'member_to_group' ? -1 : 1;
  if (t.type === 'guest_session_fee') return t.status === 'collected' ? 1 : 0;
  if (FUND_IN_TYPES.includes(t.type)) return 1;
  if (FUND_OUT_TYPES.includes(t.type)) return -1;
  return 0; // fund-neutral: coin_allot, coin_transfer, settlement, direct_payment_info…
}

function fundEffect(t) {
  if (t.reversed) return 0;
  if (t.approval_status && t.approval_status !== 'approved') return 0;
  if (['contribution', 'guest_contribution'].includes(t.type) && (t.status || 'received') === 'pledged') return 0;
  if (ZERO_EFFECT_REVERSALS.includes(t.type)) return 0;
  return fundDirection(t) * (t.amount || 0);
}

export default function FundTab({ ctx, view = 'all', typeConfig = null }) {
  const showSummary = view === 'all' || view === 'summary';
  const showTransactions = view === 'all' || view === 'transactions';
  // iter189 — type-aware widget gating. Monthly momentum is noise for sports
  // (weekly cadence) and trips (one-off); category breakdown is duplicated
  // by TripBudgetCard for trips. Daily/event keep both.
  const showMonthlyMomentum = !typeConfig || typeConfig.isDaily || typeConfig.isEvent;
  const showWeeklyFinesWidget = !typeConfig || typeConfig.isSports;
  // iter258 — ledger search + filters (Money tab only)
  const [txnQuery, setTxnQuery] = useState('');
  const [txnTypeFilter, setTxnTypeFilter] = useState('all');
  const [txnMemberFilter, setTxnMemberFilter] = useState('all');
  const {
    group,
    user,
    isAdmin,
    groupId,
    fetchFund,
    fundData,
    fundSnapshot,
    weeklyStats,
    pendingTransfers,
    expandedTransferId,
    setExpandedTransferId,
    fineDefaults,
    balancePolicy,
    selectedTxns,
    bulkReversing,
    importLoading,
    formatCurrency,
    downloadBlob,
    handleRepayLoan,
    handleMarkPledgeReceived,
    handleApproveTransfer,
    handleRejectTransfer,
    handleCancelTransfer,
    setShowFundModal,
    openFineWithType,
    setShowAllotCoins,
    setShowTransferModal,
    setShowGuestContribModal,
    setShowLoanToMemberModal,
    openQuickSettle,
    handleImportFile,
    handleImportFines,
    handleDownloadSample,
    setShowFineDefaultsModal,
    setShowBalancePolicyModal,
    handleResetGroup,
    toggleSelectAllTxns,
    handleBulkReverse,
    toggleTxnSelect,
    handleReverseTransaction,
  } = ctx;

  return (
    <div data-testid="fund-tab-body" className="space-y-3">
      {showTransactions && (
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-display font-bold tracking-tight">Fund Transactions</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => downloadBlob(`/api/exports/fund-transactions/${groupId}?format=excel&days=90`, 'fund_transactions.xlsx')} data-testid="download-fund-excel-btn" className="rounded-full text-xs">
            <Download className="w-3.5 h-3.5 mr-1" /> Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => downloadBlob(`/api/exports/fund-transactions/${groupId}?format=pdf&days=90`, 'fund_transactions.pdf')} data-testid="download-fund-pdf-btn" className="rounded-full text-xs">
            <Download className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
          {isAdmin && (
            <Button
              size="sm"
              onClick={async () => {
                if (!window.confirm('Send the Group Fund Ledger PDF (last 90 days) to ALL members on WhatsApp?')) return;
                try {
                  const res = await api.post(`/whatsapp/groups/${groupId}/send-fund-ledger?days=90`);
                  toast.success(res.data?.message || 'Fund ledger sent');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to send');
                }
              }}
              data-testid="send-fund-ledger-btn"
              className="rounded-full text-xs"
            >
              <Send className="w-3.5 h-3.5 mr-1" /> Send to group
            </Button>
          )}
        </div>
      </div>
      )}

      {/* iter293 — headline strip: Net Position (matches the PDF headline) + Book Balance */}
      {fundData && (
        <div className="grid grid-cols-2 gap-2" data-testid="fund-headline-strip">
          <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-3">
            <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Net Position</p>
            <p className="text-xl font-display font-bold" data-testid="fund-net-position">{formatNXC(fundData.net_position ?? fundData.fund_balance ?? 0)}</p>
            <p className="text-xs text-muted-foreground">Cash + members to pay − group to pay back</p>
          </div>
          <div className="rounded-2xl border-2 p-3">
            <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Book Balance</p>
            <p className="text-xl font-display font-bold" data-testid="fund-book-balance">{formatNXC(fundData.fund_balance ?? 0)}</p>
            <p className="text-xs text-muted-foreground">Incl. fees charged but not yet collected</p>
          </div>
        </div>
      )}
      {showSummary && <>
      {/* Stage-2 Open Loans card — both directions surfaced in one place with Repay Now buttons */}
      {fundData && fundData.open_loans_count > 0 && (
        <div
          className="rounded-2xl border-2 border-blue-500/30 bg-gradient-to-br from-blue-500/5 via-background to-background p-4"
          data-testid="open-loans-card"
        >
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-blue-700/90 font-bold flex items-center gap-1.5">
                <NXCIcon className="w-3 h-3" />Open Loans ({fundData.open_loans_count})
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">
                Group to pay back {formatNXC(fundData.loans_to_group_outstanding || 0)} · Members to pay {formatNXC(fundData.loans_from_group_outstanding || 0)}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {(fundData.transactions || [])
              .filter((t) => t.is_loan && (t.loan_status === 'open' || t.loan_status === 'partial') && !t.reversed)
              .slice(0, 8)
              .map((t) => {
                const principal = t.loan_principal ?? t.amount ?? 0;
                const repaid = t.loan_repaid_amount ?? 0;
                const outstanding = Math.max(0, principal - repaid);
                const isMtg = t.loan_direction === 'member_to_group';
                return (
                  <div key={t.id} className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-border/40 last:border-0" data-testid={`loan-row-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate flex items-center gap-1.5">
                        {t.user_name || 'Unknown'}
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold uppercase ${isMtg ? 'bg-emerald-500/15 text-emerald-700' : 'bg-orange-500/15 text-orange-700'}`}>
                          {isMtg ? 'Group to pay back' : 'To pay the group'}
                        </span>
                      </div>
                      <div className="text-muted-foreground text-xs truncate">
                        {t.note || (isMtg ? 'Loan to group' : 'Loan from group')}
                        {t.expected_repayment_date && ` · due ${t.expected_repayment_date}`}
                        {t.loan_status === 'partial' && ` · ${formatNXC(repaid)}/${formatNXC(principal)} repaid`}
                      </div>
                    </div>
                    <div className="font-bold text-blue-700 whitespace-nowrap">{formatNXC(outstanding)}</div>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRepayLoan(t.id, principal, repaid, t.user_name)}
                        data-testid={`repay-loan-${t.id}`}
                        className="rounded-full h-7 text-xs px-2"
                      >
                        Repay Now
                      </Button>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Cash in hand vs Receivables — surfaces "payment pending" (promised but not yet collected) money.
          iter150 — also renders when accrued session-fees or unpaid fines are
          riding inside fund_balance, so users see Cash ≠ Fund even when no
          pledged contributions are outstanding.
          iter153 — also renders when reimburses are owed to members (member
          fronted cash) and the breakdown subtracts that side too. */}
      {fundData && (
        fundData.receivables > 0 ||
        fundData.pledged_count > 0 ||
        (fundData.accrued_session_fees || 0) > 0 ||
        (fundData.accrued_fines_outstanding || 0) > 0 ||
        (fundData.accrued_reimburses_owed_members || 0) > 0
      ) && (
        <div
          className="rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-background to-background p-4"
          data-testid="receivables-card"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Cash in hand</div>
              <div className="text-2xl font-display font-bold text-primary" data-testid="cash-in-hand">
                {formatNXC(fundData.cash_in_hand ?? 0)}
              </div>
            </div>
            <div className="w-px h-10 bg-border" />
            <div className="flex-1">
              {(fundData.receivables > 0 || fundData.pledged_count > 0) ? (
                <>
                  <div className="text-xs uppercase tracking-wider text-amber-700/90 font-bold">
                    Payment Pending ({fundData.pledged_count} {fundData.pledged_count === 1 ? 'item' : 'items'})
                  </div>
                  <div className="text-2xl font-display font-bold text-amber-700" data-testid="receivables-amount">
                    {formatNXC(fundData.receivables ?? 0)}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-xs uppercase tracking-wider text-amber-700/90 font-bold">
                    Net Accrued (in Fund Balance)
                  </div>
                  <div className="text-2xl font-display font-bold text-amber-700" data-testid="accrued-amount">
                    {formatNXC(
                      (fundData.accrued_session_fees || 0)
                      + (fundData.accrued_fines_outstanding || 0)
                      - (fundData.accrued_reimburses_owed_members || 0)
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 tabular-nums leading-tight">
                    {(fundData.accrued_session_fees || 0) > 0 && (
                      <>Session fees <span className="text-emerald-700">{formatNXC(fundData.accrued_session_fees)}</span></>
                    )}
                    {((fundData.accrued_session_fees || 0) > 0 && (fundData.accrued_fines_outstanding || 0) > 0) && ' · '}
                    {(fundData.accrued_fines_outstanding || 0) > 0 && (
                      <>Fines <span className="text-emerald-700">{formatNXC(fundData.accrued_fines_outstanding)}</span></>
                    )}
                    {((((fundData.accrued_session_fees || 0) > 0) || ((fundData.accrued_fines_outstanding || 0) > 0)) && (fundData.accrued_reimburses_owed_members || 0) > 0) && ' · '}
                    {(fundData.accrued_reimburses_owed_members || 0) > 0 && (
                      <>Owe members <span className="text-red-600">−{formatNXC(fundData.accrued_reimburses_owed_members)}</span></>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          {isAdmin && (
            <div className="mt-3 pt-3 border-t border-border/50 space-y-1.5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Outstanding payments</div>
              {(fundData.transactions || [])
                .filter((t) => (t.type === 'contribution' || t.type === 'guest_contribution')
                  && (t.status || 'received') === 'pledged' && !t.reversed)
                .slice(0, 6)
                .map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-2 text-xs py-1" data-testid={`pledged-row-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{t.user_name || t.guest_name || 'Unknown'}</div>
                      <div className="text-muted-foreground text-xs truncate">{t.note || (t.type === 'guest_contribution' ? 'Guest payment pending' : 'Payment pending')}</div>
                    </div>
                    <div className="font-bold text-amber-700 whitespace-nowrap">{formatNXC(t.amount)}</div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMarkPledgeReceived(t.id, Math.round(t.amount), t.user_name || t.guest_name)}
                      data-testid={`mark-received-${t.id}`}
                      className="rounded-full h-7 text-xs px-2"
                    >
                      Mark Received
                    </Button>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Group-level guest fees rollup — so guest money isn't forgotten */}
      <PendingGuestFeesCard groupId={groupId} isAdmin={isAdmin} onChange={fetchFund} />

      {/* Fund snapshot card (iter44) — MTD inflow / outflow / net.
          iter189 — gated by typeConfig: hidden on sports + trip (no value). */}
      {showMonthlyMomentum && fundSnapshot && fundSnapshot.txn_count > 0 && (
        <div
          className="rounded-2xl border-2 border-primary/15 bg-gradient-to-br from-primary/5 via-background to-background p-4 space-y-3"
          data-testid="fund-snapshot-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5" /> {fundSnapshot.month_label} momentum
              </div>
              <div className="text-xs text-muted-foreground/70">{fundSnapshot.txn_count} transactions this month</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-2.5" data-testid="snapshot-inflow">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold">Inflow</div>
              <div className="text-lg font-bold text-emerald-700">
                {formatNXC(fundSnapshot.inflow)}
              </div>
            </div>
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-2.5" data-testid="snapshot-outflow">
              <div className="text-xs uppercase tracking-wider text-red-700 font-bold">Outflow</div>
              <div className="text-lg font-bold text-red-700">
                {formatNXC(fundSnapshot.outflow)}
              </div>
            </div>
            <div className={`rounded-xl border p-2.5 ${fundSnapshot.net >= 0 ? 'bg-primary/10 border-primary/30' : 'bg-amber-500/10 border-amber-500/30'}`} data-testid="snapshot-net">
              <div className={`text-xs uppercase tracking-wider font-bold ${fundSnapshot.net >= 0 ? 'text-primary' : 'text-amber-700'}`}>Net</div>
              <div className={`text-lg font-bold ${fundSnapshot.net >= 0 ? 'text-primary' : 'text-amber-700'}`}>
                {fundSnapshot.net >= 0 ? '+' : '−'}{formatNXC(Math.abs(fundSnapshot.net)).replace(/^−/, '')}
              </div>
            </div>
          </div>
          {fundSnapshot.top_categories?.length > 0 && (
            <div className="pt-1 border-t border-border/50">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1">Top spend categories</div>
              <div className="flex flex-wrap gap-1.5" data-testid="snapshot-top-categories">
                {fundSnapshot.top_categories.map((c) => (
                  <span key={c.category} className="text-xs px-2 py-0.5 rounded-full bg-muted/60 border border-border">
                    {c.category}
                    <span className="ml-1 font-bold">{formatNXC(c.amount)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Weekly Fine Leaderboard / Quick Stats Widget — hidden for types without fines.
          iter189 — also hidden on trip/daily/event (only useful on sports). */}
      {showWeeklyFinesWidget && weeklyStats && (group?.features?.fines !== false) && (weeklyStats.this_week.count > 0 || weeklyStats.last_week.count > 0) && (
        <div
          className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background p-4 space-y-3"
          data-testid="fund-weekly-stats"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5" /> This Week's Fines
              </div>
              <div className="text-xs text-muted-foreground/70">{weeklyStats.week_label}</div>
            </div>
            {weeklyStats.last_week.total > 0 && (
              <div
                className={`text-xs font-bold rounded-full px-3 py-1 ${
                  weeklyStats.pct_change > 0
                    ? 'bg-red-500/15 text-red-600 dark:text-red-400'
                    : weeklyStats.pct_change < 0
                    ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                    : 'bg-muted text-muted-foreground'
                }`}
                data-testid="weekly-stats-pct"
              >
                {weeklyStats.pct_change > 0 ? '↑' : weeklyStats.pct_change < 0 ? '↓' : '→'}{' '}
                {Math.abs(weeklyStats.pct_change)}% vs last week
              </div>
            )}
          </div>

          <div className="flex items-end gap-4">
            <div>
              <div className="text-3xl font-bold">
                {formatNXC(weeklyStats.this_week.total)}
              </div>
              <div className="text-xs text-muted-foreground">{weeklyStats.this_week.count} fines collected</div>
            </div>
            {/* Tiny daily sparkline */}
            {weeklyStats.daily_totals && (
              <div className="flex items-end gap-1 h-12 flex-1 justify-end">
                {weeklyStats.daily_totals.map((d, idx) => {
                  const max = Math.max(1, ...weeklyStats.daily_totals.map((x) => x.amount));
                  const heightPct = (d.amount / max) * 100;
                  return (
                    <div key={idx} className="flex flex-col items-center gap-0.5 w-7">
                      <div
                        className={`w-full rounded-t-sm ${d.amount > 0 ? 'bg-primary/70' : 'bg-muted'}`}
                        style={{ height: `${Math.max(heightPct, 4)}%`, minHeight: '2px' }}
                        title={`${d.day}: ${formatNXC(d.amount)}`}
                      />
                      <span className="text-xs text-muted-foreground/70">{d.day[0]}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top Offenders Leaderboard */}
          {weeklyStats.top_offenders?.length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-border/50">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Top Offenders 🏆</div>
              <div className="space-y-1" data-testid="top-offenders-list">
                {weeklyStats.top_offenders.map((o, idx) => {
                  const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉';
                  return (
                    <div key={o.user_id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{medal}</span>
                        <span className="font-semibold">{o.name}</span>
                        <span className="text-xs text-muted-foreground">({o.count} fines)</span>
                      </div>
                      <span className="font-mono font-bold text-red-500">−{formatCurrency(o.amount)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending Money Transfer requests */}
      {pendingTransfers.length > 0 && (
        <div className="space-y-2" data-testid="pending-transfers-list">
          {pendingTransfers.map((t) => {
            // iter105: 4-state, direction-aware role resolution.
            //   send    → payer = from_user_id (sender), approver = to_user_id (recipient)
            //   request → payer = from_user_id (the one being asked), approver = same payer; requester = to_user_id
            // The party who MUST tap Approve/Reject is the "payer" in request flow,
            // and the "recipient" in send flow — i.e. the one OTHER than the initiator.
            const isRequest = t.direction === 'request';
            const isFundPayer = isRequest && t.from_user_id === 'group_fund';
            // For group-fund requests, ANY admin can approve. For peer requests,
            // only the named payer (from_user_id) can. For send flow, the recipient.
            const isApprover = isRequest
              ? (isFundPayer ? !!isAdmin : t.from_user_id === user?.id)
              : t.to_user_id === user?.id;
            const isInitiator = isRequest
              ? (t.requested_by ? t.requested_by === user?.id : t.to_user_id === user?.id)
              : t.from_user_id === user?.id;
            const isOpen = expandedTransferId === t.id;
            const amount = formatNXC(t.amount, group?.currency);

            // Headline text per role.
            let headline;
            if (isApprover && isRequest) {
              headline = `${t.to_user_name} requests ${amount} from you`;
            } else if (isApprover && !isRequest) {
              headline = `${t.from_user_name} wants to transfer ${amount} to you`;
            } else if (isInitiator && isRequest) {
              const payerLabel = isFundPayer ? 'Group fund' : t.from_user_name;
              headline = `You requested ${amount} from ${payerLabel} · awaiting approval`;
            } else if (isInitiator && !isRequest) {
              headline = `You're transferring ${amount} to ${t.to_user_name} · awaiting their approval`;
            } else {
              // Bystander view (e.g. an admin who can see a fund-request they didn't raise but isn't approving).
              headline = isRequest
                ? `${t.to_user_name} requests ${amount} from ${isFundPayer ? 'group fund' : t.from_user_name}`
                : `${t.from_user_name} transferring ${amount} to ${t.to_user_name}`;
            }

            return (
              <div key={t.id} className="rounded-xl border-2 border-orange-500/40 bg-orange-500/5 p-3 space-y-2" data-testid={`transfer-${t.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate" data-testid={`transfer-headline-${t.id}`}>
                      {headline}
                    </div>
                    {t.note && <div className="text-xs text-muted-foreground truncate">{t.note}</div>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {isApprover && (
                      <>
                        <Button size="sm" onClick={() => handleApproveTransfer(t.id)} className="rounded-full h-8 text-xs" data-testid={`approve-transfer-${t.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => handleRejectTransfer(t.id)} className="rounded-full h-8 text-xs" data-testid={`reject-transfer-${t.id}`}>Reject</Button>
                      </>
                    )}
                    {isInitiator && !isApprover && (
                      <Button size="sm" variant="ghost" onClick={() => handleCancelTransfer(t.id)} className="rounded-full h-8 text-xs" data-testid={`cancel-transfer-${t.id}`}>Cancel</Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setExpandedTransferId(isOpen ? null : t.id)}
                      className="rounded-full h-8 text-xs px-2"
                      data-testid={`comment-toggle-${t.id}`}
                      title="Comments"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {isOpen && (
                  <div className="pt-2 border-t border-orange-500/20" data-testid={`transfer-thread-${t.id}`}>
                    <CommentThread entityType="coin_transfer" entityId={t.id} compact />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>}

      {/* iter94 — admin actions (Top Up, fines, Allot, Transfer, Loans, Imports, etc.)
          have moved to the global `+` Floating Action Button (AdminActionsFab).
          Removing the cluttered horizontally-scrolling chip row that used to sit here. */}

      {showTransactions && <>
      {/* Balance Warning Banner */}
      {isAdmin && balancePolicy.warning_threshold != null && fundData?.coin_balances && (() => {
        const warned = Object.entries(fundData.coin_balances)
          .filter(([, bal]) => bal < balancePolicy.warning_threshold)
          .map(([uid, bal]) => {
            const m = group?.members?.find(mem => mem.id === uid);
            return m ? `${m.name} (${Math.round(bal)})` : null;
          })
          .filter(Boolean);
        return warned.length > 0 ? (
          <Card className="p-3 border-2 rounded-2xl bg-red-500/5 border-red-500/30" data-testid="balance-warning-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-600">Balance Warning</p>
                <p className="text-xs text-muted-foreground">
                  Below threshold ({Math.round(balancePolicy.warning_threshold)}): {warned.join(', ')}
                </p>
              </div>
            </div>
          </Card>
        ) : null;
      })()}

      {!fundData?.transactions?.length ? (
        <EmptyState
          illustration={EmptyFund}
          title="No fund activity yet"
          description={isAdmin ? 'Add a contribution to spin up the group fund.' : 'No fund movements have happened yet.'}
          testId="no-fund-transactions"
        />
      ) : (
        <div className="space-y-2">
          {/* iter258 — search + filters */}
          <div className="space-y-2" data-testid="txn-filters">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={txnQuery}
                onChange={(e) => setTxnQuery(e.target.value)}
                placeholder="Search transactions — note, name, amount…"
                data-testid="txn-search-input"
                className="w-full h-10 pl-9 pr-3 border-2 rounded-xl bg-background text-sm focus:outline-none focus:border-primary/50"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={txnTypeFilter}
                onChange={(e) => setTxnTypeFilter(e.target.value)}
                data-testid="txn-type-filter"
                className="flex-1 h-9 px-2 border-2 rounded-xl bg-background text-xs font-semibold"
              >
                <option value="all">All types</option>
                <option value="contribution">Contributions</option>
                <option value="expense">Expenses</option>
                <option value="fine">Fines</option>
                <option value="session_fee">Session fees</option>
                <option value="loan">Loans</option>
                <option value="other">Other</option>
              </select>
              <select
                value={txnMemberFilter}
                onChange={(e) => setTxnMemberFilter(e.target.value)}
                data-testid="txn-member-filter"
                className="flex-1 h-9 px-2 border-2 rounded-xl bg-background text-xs font-semibold"
              >
                <option value="all">All members</option>
                {(group?.members || []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Select All + Bulk Reverse */}
          {isAdmin && (
            <div className="flex items-center justify-between px-1 py-1">
              <button onClick={toggleSelectAllTxns} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="select-all-txns">
                {selectedTxns.size > 0 ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
                {selectedTxns.size > 0 ? `${selectedTxns.size} selected` : 'Select all'}
              </button>
              {selectedTxns.size > 0 && (
                <Button size="sm" variant="outline" onClick={handleBulkReverse} disabled={bulkReversing} data-testid="bulk-reverse-btn" className="rounded-full text-xs">
                  <RefreshCw className="w-3 h-3 mr-1" />
                  {bulkReversing ? 'Reversing...' : `Reverse ${selectedTxns.size}`}
                </Button>
              )}
            </div>
          )}
          {(() => {
            // Iter44: compute running balance walking oldest→newest then attach to each row
            // (txns arrive sorted newest-first from backend)
            // iter297 — rewritten: fundEffect() mirrors the backend fund math for
            // EVERY type (was missing session/membership fees, advances, loans,
            // cash-out…), and the walk is anchored to the canonical fund_balance
            // so the newest row's bal always equals the app's Book Balance.
            const txns = fundData.transactions;
            const oldestFirst = [...txns].reverse();
            const windowNet = oldestFirst.reduce((s, t) => s + fundEffect(t), 0);
            let running = (fundData.fund_balance ?? 0) - windowNet; // opening balance
            const balanceMap = new Map();
            for (const t of oldestFirst) {
              running += fundEffect(t);
              balanceMap.set(t.id, Math.round(running * 100) / 100);
            }
            // iter258 — apply search + filters AFTER running balance (balances stay true)
            const TYPE_BUCKETS = {
              contribution: ['contribution', 'guest_contribution'],
              expense: ['expense_deduct', 'expense_reversal'],
              fine: ['fine', 'fine_reversal'],
              session_fee: ['session_fee', 'session_fee_reversal'],
              loan: ['loan_disbursed', 'loan_repayment'],
            };
            const q = txnQuery.trim().toLowerCase();
            const filteredTxns = txns.filter((t) => {
              if (txnTypeFilter !== 'all') {
                const bucket = TYPE_BUCKETS[txnTypeFilter];
                if (bucket ? !bucket.includes(t.type) : Object.values(TYPE_BUCKETS).flat().includes(t.type)) return false;
              }
              if (txnMemberFilter !== 'all' && t.user_id !== txnMemberFilter) return false;
              if (q) {
                const hay = `${t.note || ''} ${t.user_name || ''} ${t.type || ''} ${Math.round(t.amount || 0)}`.toLowerCase();
                if (!hay.includes(q)) return false;
              }
              return true;
            });
            if (filteredTxns.length === 0) {
              return (
                <p className="text-center text-sm text-muted-foreground py-8" data-testid="txn-no-results">
                  No transactions match — try clearing the search or filters.
                </p>
              );
            }
            return filteredTxns.map((txn, idx) => {
              const runningBal = balanceMap.get(txn.id);
              return (
                <motion.div
                  key={txn.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, delay: Math.min(idx, 14) * 0.04 }}
                >
                <Card className={`p-3 border-2 rounded-xl ${txn.reversed ? 'opacity-50' : ''} ${selectedTxns.has(txn.id) ? 'border-primary/50 bg-primary/5' : ''}`} data-testid={`fund-txn-${txn.id}`}>
                  <div className="flex items-center gap-2">
                    {isAdmin && ['fine', 'coin_allot', 'contribution', 'session_fee'].includes(txn.type) && !txn.reversed && (
                      <button onClick={() => toggleTxnSelect(txn.id)} className="shrink-0" data-testid={`select-txn-${txn.id}`}>
                        {selectedTxns.has(txn.id)
                          ? <CheckSquare className="w-4 h-4 text-primary" />
                          : <Square className="w-4 h-4 text-muted-foreground" />}
                      </button>
                    )}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      txn.type.includes('reversal') ? 'bg-gray-500/20 text-gray-500' :
                      txn.type === 'contribution' ? 'bg-green-500/20 text-green-600' :
                      txn.type === 'fine' ? 'bg-yellow-500/20 text-yellow-600' :
                      txn.type === 'coin_allot' ? 'bg-blue-500/20 text-blue-600' :
                      'bg-red-500/20 text-red-600'
                    }`}>
                      {txn.type === 'contribution' ? <Plus className="w-4 h-4" /> :
                       txn.type === 'fine' ? <AlertTriangle className="w-4 h-4" /> :
                       txn.type === 'coin_allot' ? <NXCIcon className="w-4 h-4" /> :
                       txn.type.includes('reversal') ? <RefreshCw className="w-4 h-4" /> :
                       <ArrowUpRight className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <p className={`text-sm font-semibold truncate ${txn.reversed ? 'line-through' : ''}`}>{txn.note}</p>
                        {txn.reversed && <StatusBadge fundTxn={txn} compact />}
                        {/* iter134 — AI approval / pending chip with rationale on hover */}
                        <AIApprovalChip txn={txn} />
                        {(txn.type === 'coin_transfer' || txn.type === 'fund_reimburse') && txn.transfer_id && (
                          <NeoPayBadge id={txn.transfer_id} type={txn.type} isAdmin={isAdmin} />
                        )}
                        {/* iter198 — Authenticity badge for non-coin_transfer fund rows
                            (fine / contribution / session_fee / coin_allot / loan / repay etc.) */}
                        {!['coin_transfer', 'fund_reimburse'].includes(txn.type) && txn.neopay_tx_id && (
                          <NeoPayBadge id={txn.id} type="fund_transaction" isAdmin={isAdmin} />
                        )}
                        {/* Iter44 — category badge for fund expenses */}
                        {txn.type === 'expense_deduct' && txn.category && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold uppercase tracking-wide"
                            data-testid={`txn-category-${txn.id}`}
                          >
                            {txn.category}
                          </span>
                        )}
                        {/* iter48 — fine_type badge so admin can scan fine list at a glance */}
                        {txn.type === 'fine' && txn.fine_type && (
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
                              txn.fine_type === 'no_show' ? 'bg-red-500/15 text-red-600' :
                              txn.fine_type === 'late_cancel' ? 'bg-orange-500/15 text-orange-600' :
                              txn.fine_type === 'cancellation' ? 'bg-orange-500/15 text-orange-600' :
                              txn.fine_type === 'late_above_10' ? 'bg-amber-500/15 text-amber-700' :
                              txn.fine_type === 'late_10' ? 'bg-amber-400/15 text-amber-600' :
                              'bg-muted text-muted-foreground'
                            }`}
                            data-testid={`txn-fine-type-${txn.id}`}
                          >
                            {({
                              no_show: 'No Show',
                              late_cancel: 'Late Cancel',
                              cancellation: 'Cancel',
                              late_above_10: 'Late >',
                              late_10: 'Late ≤',
                            })[txn.fine_type] || txn.fine_type.replace(/_/g, ' ')}
                          </span>
                        )}
                        {/* iter48 — money allotment badge */}
                        {txn.type === 'coin_allot' && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-600 font-semibold uppercase tracking-wide"
                            data-testid={`txn-allot-badge-${txn.id}`}
                          >
                            money
                          </span>
                        )}
                        {/* iter48 — contribution (top-up) badge */}
                        {txn.type === 'contribution' && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 font-semibold uppercase tracking-wide"
                            data-testid={`txn-contribution-badge-${txn.id}`}
                          >
                            Cash In
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {txn.user_name} • {new Date(txn.created_at).toLocaleDateString()}
                        {typeof runningBal === 'number' && (
                          <>
                            {' • '}
                            <span className="font-mono" data-testid={`running-balance-${txn.id}`}>
                              bal {formatNXC(runningBal)}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {(() => {
                        const dir = fundDirection(txn);
                        return (
                          <p className={`font-display font-bold text-sm ${
                            dir < 0 ? 'text-negative' : dir > 0 ? 'text-positive' : 'text-muted-foreground'
                          }`} data-testid={`txn-amount-${txn.id}`}>
                            {dir < 0 ? '-' : dir > 0 ? '+' : ''}{formatCurrency(txn.amount)}
                          </p>
                        );
                      })()}
                      {isAdmin && ['fine', 'coin_allot', 'contribution', 'session_fee'].includes(txn.type) && !txn.reversed && (
                        <button
                          onClick={() => handleReverseTransaction(txn.id)}
                          className="w-6 h-6 rounded-md bg-gray-500/10 text-gray-500 flex items-center justify-center hover:bg-red-500/10 hover:text-red-500 transition-colors ml-1"
                          data-testid={`reverse-txn-${txn.id}`}
                          title="Reverse"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
                </motion.div>
              );
            });
          })()}
        </div>
      )}
      </>}
    </div>
  );
}
